import * as React from 'react';
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, LinearProgress, MenuItem, Stack, Switch, TextField } from '@mui/material';
import { VIRTUAL_FOOTBALL_ID, VirtualCatalogSportDto } from '../../models/dtos/VirtualSportsCatalogDto';
import VirtualCountrySelect from './VirtualCountrySelect';

export interface VirtualImmediateGenerationOptions {
    generatePortraits: boolean;
    countryCode?: string;
    description?: string;
}

export default function VirtualImmediateGenerationDialog(props: {
    open: boolean;
    entityLabel: 'team' | 'league' | 'player';
    sports: VirtualCatalogSportDto[];
    onClose: () => void;
    onGenerate: (
        sportId: number,
        count: number,
        onProgress: (completed: number) => void,
        options: VirtualImmediateGenerationOptions,
    ) => Promise<void>;
}) {
    const [sportId, setSportId] = React.useState<number | ''>('');
    const [count, setCount] = React.useState<number | ''>(1);
    const [busy, setBusy] = React.useState(false);
    const [completed, setCompleted] = React.useState(0);
    const [generatePortraits, setGeneratePortraits] = React.useState(true);
    const [teamCountryCode, setTeamCountryCode] = React.useState('');
    const [teamDescription, setTeamDescription] = React.useState('');
    const [error, setError] = React.useState('');
    const asksForCount = props.entityLabel === 'player';
    const asksForTeamHints = props.entityLabel === 'team';
    const validCount = !asksForCount || (count !== '' && Number.isInteger(count) && count >= 1 && count <= 100);

    React.useEffect(() => {
        if (props.open) {
            setSportId(props.sports.some(sport => sport.id === VIRTUAL_FOOTBALL_ID) ? VIRTUAL_FOOTBALL_ID : '');
            setCount(1);
            setCompleted(0);
            setGeneratePortraits(true);
            setTeamCountryCode('');
            setTeamDescription('');
            setError('');
        }
    }, [props.open, props.sports]);

    const generate = async () => {
        if (sportId === '' || !validCount) return;
        setBusy(true);
        setCompleted(0);
        setError('');
        try {
            await props.onGenerate(sportId, count === '' ? 1 : count, setCompleted, {
                generatePortraits,
                countryCode: teamCountryCode || undefined,
                description: teamDescription.trim() || undefined,
            });
            props.onClose();
        } catch (reason) {
            setError(reason instanceof Error ? reason.message : 'Generation failed');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={props.open} onClose={busy ? undefined : props.onClose} fullWidth maxWidth="sm">
            <DialogTitle>Generate {props.entityLabel} immediately</DialogTitle>
            <DialogContent>
                {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
                <TextField
                    select
                    required
                    fullWidth
                    margin="normal"
                    label="Virtual sport"
                    value={sportId}
                    onChange={event => setSportId(Number(event.target.value))}
                >
                    {props.sports.map(sport => <MenuItem key={sport.id} value={sport.id}>{sport.name}</MenuItem>)}
                </TextField>
                {asksForTeamHints ? <Stack spacing={2} sx={{ mt: 1 }}>
                    <VirtualCountrySelect
                        value={teamCountryCode}
                        onChange={setTeamCountryCode}
                        disabled={busy}
                        required={false}
                        label="Country (optional)"
                    />
                    <TextField
                        multiline
                        minRows={3}
                        label="Team generation description (optional)"
                        value={teamDescription}
                        onChange={event => setTeamDescription(event.target.value)}
                        disabled={busy}
                        inputProps={{ maxLength: 2000 }}
                        helperText={`${teamDescription.length}/2000 · Creative direction for the team name and biography`}
                    />
                </Stack> : null}
                {asksForCount ? <TextField
                    required
                    fullWidth
                    type="number"
                    margin="normal"
                    label="Number of players"
                    value={count}
                    onChange={event => setCount(event.target.value === '' ? '' : Number(event.target.value))}
                    inputProps={{ min: 1, max: 100, step: 1 }}
                    error={count !== '' && !validCount}
                    helperText="Choose between 1 and 100 players"
                /> : null}
                {asksForCount ? <FormControlLabel
                    sx={{ mt: 1, display: 'flex' }}
                    control={<Switch
                        checked={generatePortraits}
                        onChange={event => setGeneratePortraits(event.target.checked)}
                        disabled={busy}
                    />}
                    label="Generate portraits"
                /> : null}
                {asksForCount ? <DialogContentText>
                    When disabled, players are created without portraits. A portrait can be generated later from the player details dialog.
                </DialogContentText> : null}
                {busy && asksForCount && count !== '' ? <>
                    <LinearProgress variant="determinate" value={(completed / count) * 100} sx={{ mt: 2 }} />
                    <DialogContentText sx={{ mt: 1 }}>
                        Generated {completed} of {count} players. Keep this dialog open while generation is running.
                    </DialogContentText>
                </> : null}
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onClose} disabled={busy}>Cancel</Button>
                <Button variant="contained" onClick={generate} disabled={sportId === '' || !validCount || busy}>
                    {busy ? <CircularProgress size={20} /> : asksForCount && typeof count === 'number' && count > 1 ? `Generate ${count} players` : 'Generate'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
